import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as api from '../lib/api';
import { vehicleKindMeta } from '../lib/fit';
import { HOME_LOCATION } from '../lib/brand';

/**
 * useSearchStore — keeps the facility map and the result list in lockstep.
 *
 * Design rules, unchanged from the discovery build because they held up:
 *  1. The map never owns state. It reads `viewport` and writes back through
 *     setViewport, so a pin and a card can never disagree.
 *  2. Hover/selection are IDs, never objects — two copies of the same facility
 *     drift apart after a refetch.
 *  3. Only the vehicle profile persists. Restoring someone into last week's
 *     filters is a bad surprise; forgetting their rig dimensions is worse.
 *  4. Every fetch carries a request token, so a slow response from an old
 *     viewport can never overwrite a newer one.
 */

const DEFAULT_VEHICLE = {
  kind: 'travel_trailer',
  length: 30,
  width: 8.5,
  height: 11,
  slideOuts: 1,
};

const DEFAULT_VIEWPORT = {
  center: { lat: HOME_LOCATION.lat, lng: HOME_LOCATION.lng },
  zoom: HOME_LOCATION.zoom,
  radiusMiles: 60,
  bounds: null,
};

const DEFAULT_FILTERS = {
  includeUnavailable: false,
  spaceTypes: [],
  amps: [],
  hasPower: false,
  hasWater: false,
  hasSewer: false,
  pullThrough: false,
  access24h: false,
  hasDumpStation: false,
  hasWashBay: false,
  minPrice: null,
  maxPrice: null,
  sort: 'distance',
  limit: 24,
  offset: 0,
};

let activeRequestToken = 0;
let debounceTimer = null;

export const useSearchStore = create()(
  persist(
    (set, get) => ({
      vehicle: { ...DEFAULT_VEHICLE },
      vehiclePanelOpen: false,
      viewport: { ...DEFAULT_VIEWPORT },
      filters: { ...DEFAULT_FILTERS },

      results: [],
      pagination: { total: 0, limit: 24, offset: 0, hasMore: false },
      status: 'idle',
      error: null,

      hoveredFacilityId: null,
      selectedFacilityId: null,

      // -- vehicle -------------------------------------------------------
      setVehicle: (patch) => {
        set((s) => ({ vehicle: { ...s.vehicle, ...patch } }));
        get().fetchResults({ debounce: true, resetPage: true });
      },

      setVehicleKind: (kind) => {
        const meta = vehicleKindMeta(kind);
        get().setVehicle({ kind, length: meta.length, width: meta.width, height: meta.height });
      },

      setVehiclePanelOpen: (open) => set({ vehiclePanelOpen: open }),

      // -- viewport ------------------------------------------------------
      setViewport: (patch) => {
        set((s) => ({ viewport: { ...s.viewport, ...patch } }));
        get().fetchResults({ debounce: true, resetPage: true });
      },

      panTo: (lat, lng, zoom) =>
        get().setViewport({ center: { lat, lng }, ...(zoom ? { zoom } : {}) }),

      // -- filters -------------------------------------------------------
      setFilter: (key, value) => {
        set((s) => ({ filters: { ...s.filters, [key]: value } }));
        get().fetchResults({ debounce: true, resetPage: true });
      },

      toggleInArray: (key, value) => {
        const current = get().filters[key] ?? [];
        get().setFilter(key, current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value]);
      },

      setPriceRange: (minPrice, maxPrice) => {
        set((s) => ({ filters: { ...s.filters, minPrice, maxPrice } }));
        get().fetchResults({ debounce: true, resetPage: true });
      },

      resetFilters: () => {
        set({ filters: { ...DEFAULT_FILTERS } });
        get().fetchResults({ resetPage: true });
      },

      // -- cross-highlighting --------------------------------------------
      setHoveredFacility: (id) => set({ hoveredFacilityId: id }),
      clearHoveredFacility: () => set({ hoveredFacilityId: null }),
      setSelectedFacility: (id) =>
        set((s) => ({ selectedFacilityId: s.selectedFacilityId === id ? null : id })),
      clearSelectedFacility: () => set({ selectedFacilityId: null }),

      // -- fetch -----------------------------------------------------------
      fetchResults: async ({ debounce = false, resetPage = false, append = false } = {}) => {
        if (resetPage && get().filters.offset !== 0) {
          set((s) => ({ filters: { ...s.filters, offset: 0 } }));
        }

        clearTimeout(debounceTimer);
        const run = async () => {
          const token = ++activeRequestToken;
          set({ status: 'loading', error: null });
          try {
            const payload = await api.searchFacilities(buildParams(get()));
            if (token !== activeRequestToken) return; // superseded
            set((s) => ({
              status: 'success',
              results: append ? [...s.results, ...payload.results] : payload.results,
              pagination: payload.pagination,
              viewport: { ...s.viewport, bounds: payload.bounds },
            }));
          } catch (err) {
            if (token !== activeRequestToken) return;
            set({ status: 'error', error: err.message ?? 'Search failed' });
          }
        };

        if (debounce) debounceTimer = setTimeout(run, 350);
        else await run();
      },

      loadMore: async () => {
        const { pagination, filters, status } = get();
        if (!pagination.hasMore || status === 'loading') return;
        set({ filters: { ...filters, offset: pagination.offset + pagination.limit } });
        await get().fetchResults({ append: true });
      },

      getActiveFilterCount: () => {
        const { filters } = get();
        return Object.keys(DEFAULT_FILTERS).reduce((count, key) => {
          if (['sort', 'limit', 'offset'].includes(key)) return count;
          const current = filters[key];
          if (Array.isArray(current)) return count + (current.length > 0 ? 1 : 0);
          return count + (current !== DEFAULT_FILTERS[key] && current !== null && current !== false ? 1 : 0);
        }, 0);
      },
    }),
    {
      name: 'parktle:search',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ vehicle: state.vehicle }),
    },
  ),
);

function buildParams({ vehicle, viewport, filters }) {
  const params = {
    lat: viewport.center.lat,
    lng: viewport.center.lng,
    radius: viewport.radiusMiles,
    sort: filters.sort,
    limit: filters.limit,
    offset: filters.offset,
  };

  if (vehicle.length) params.vehicle_length = vehicle.length;
  if (vehicle.width) params.vehicle_width = vehicle.width;
  if (vehicle.height) params.vehicle_height = vehicle.height;

  if (filters.spaceTypes.length) params.space_type = filters.spaceTypes.join(',');
  if (filters.amps.length) params.amps = filters.amps.join(',');
  if (filters.minPrice != null) params.min_price = filters.minPrice;
  if (filters.maxPrice != null) params.max_price = filters.maxPrice;

  // Off by default: someone looking for a space should not be shown yards with
  // nothing to rent. On when they ask, after a search comes back empty.
  if (filters.includeUnavailable) params.include_unavailable = true;

  for (const [key, param] of Object.entries({
    hasPower: 'has_power', hasWater: 'has_water', hasSewer: 'has_sewer',
    pullThrough: 'pull_through', access24h: 'access_24h',
    hasDumpStation: 'has_dump_station', hasWashBay: 'has_wash_bay',
  })) {
    if (filters[key]) params[param] = true;
  }

  return params;
}

export const useVehicle = () => useSearchStore((s) => s.vehicle);
export const useIsHovered = (id) => useSearchStore((s) => s.hoveredFacilityId === id);
export const useIsSelected = (id) => useSearchStore((s) => s.selectedFacilityId === id);

export { DEFAULT_VEHICLE, DEFAULT_FILTERS, DEFAULT_VIEWPORT };
